import {Component, inject, input, OnChanges} from '@angular/core';
import {NgClass} from "@angular/common";
import {AgGridAngular} from "ag-grid-angular";
import {MatIcon} from "@angular/material/icon";
import {MatDialog} from "@angular/material/dialog";
import {GridReadyEvent, RowClickedEvent} from "ag-grid-community";
import {Subscription} from "rxjs";
import {EmailAuditService} from "../../services/email-audit.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {EmailAuditTable} from "../../table-definitions/email-audit-table";
import {IEmailAuditFilter} from "../../interfaces/email-audit/IEmailAuditFilter";
import {IEmailAuditPagedResponse} from "../../interfaces/email-audit/IEmailAuditPagedResponse";
import {IEmailAuditSummary} from "../../interfaces/email-audit/IEmailAuditSummary";
import {
  EmailAuditDetailComponent,
  IEmailAuditDetailDialogData
} from "../dialogs/email-audit-detail/email-audit-detail.component";

export type EmailAuditTableFilter = Omit<IEmailAuditFilter, 'pageNumber' | 'pageSize'>;

@Component({
  selector: 'app-email-audit-table',
  standalone: true,
  imports: [
    AgGridAngular,
    MatIcon,
    NgClass
  ],
  templateUrl: './email-audit-table.component.html'
})
export class EmailAuditTableComponent implements OnChanges {
  private emailAuditService = inject(EmailAuditService);
  private errorHandlingService = inject(ErrorHandlingService);
  private emailAuditTable = inject(EmailAuditTable);
  private matDialog = inject(MatDialog);

  public filter = input.required<EmailAuditTableFilter>();
  public includeGameColumn = input(false);

  private readonly pageSize = 15;
  private loadSubscription?: Subscription;

  public pagedResponse: IEmailAuditPagedResponse | null = null;
  public rows: IEmailAuditSummary[] = [];
  public columnDefs = this.emailAuditTable.columnDefs(false);

  protected fromNumber = 0;
  protected toNumber = 0;
  protected totalRecords = 0;
  protected pageNumber = 0;
  protected totalPages = 0;

  ngOnChanges() {
    this.columnDefs = this.emailAuditTable.columnDefs(this.includeGameColumn());
    this.loadPage(1);
  }

  onGridReady(paramsP: GridReadyEvent<IEmailAuditSummary>) {
    paramsP.api.sizeColumnsToFit({defaultMinWidth: 90});
  }

  // Cancels the request in flight, so a slower earlier response can't overwrite a newer filter's results.
  loadPage(pageNumberP: number) {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.emailAuditService.getEmailAudits({...this.filter(), pageNumber: pageNumberP, pageSize: this.pageSize}).subscribe({
      next: (responseP) => {
        this.pagedResponse = responseP;
        this.rows = responseP.Data;
        this.updatePaginator();
      },
      error: (errorP) => {
        this.errorHandlingService.displayPageLevelErrorMessage(errorP);
      }
    });
  }

  onBtFirst() {
    if (this.pageNumber > 1) {
      this.loadPage(1);
    }
  }

  onBtPrevious() {
    if (this.pageNumber > 1) {
      this.loadPage(this.pageNumber - 1);
    }
  }

  onBtNext() {
    if (this.pageNumber < this.totalPages) {
      this.loadPage(this.pageNumber + 1);
    }
  }

  onBtLast() {
    if (this.pageNumber < this.totalPages) {
      this.loadPage(this.totalPages);
    }
  }

  onRowClicked(eventP: RowClickedEvent<IEmailAuditSummary>) {
    const dialog_data: IEmailAuditDetailDialogData = {emailAuditId: eventP.data!.Id};

    this.matDialog.open(EmailAuditDetailComponent, {
      data: dialog_data,
      width: "800px"
    });
  }

  private updatePaginator() {
    this.pageNumber = this.pagedResponse!.PageNumber;
    this.totalPages = this.pagedResponse!.TotalPages;
    this.totalRecords = this.pagedResponse!.TotalRecords;
    this.fromNumber = this.totalRecords === 0 ? 0 : (this.pageNumber - 1) * this.pagedResponse!.PageSize + 1;
    this.toNumber = Math.min(this.pageNumber * this.pagedResponse!.PageSize, this.totalRecords);
  }
}
